///updated theme: capture 20 top before and 20 after + click attribution (in same file)//
(() => {
  // -------------------------
  // Utilities
  // -------------------------
  const getRootPath = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getConfig = () => {
    const el = document.getElementById('VariantSegmentConfig');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch {
      return null;
    }
  };

  // -------------------------
  // Loading state management
  // -------------------------
  const setClass = (cls, on) => document.documentElement.classList.toggle(cls, !!on);
  const setGridLoading = on => setClass('variant-segment-grid-loading', on);
  const unhideInitial = () => setClass('variant-segment-initial-loading', false);

  let safetyTimer = null;
  const startSafetyTimer = ms => {
    clearTimeout(safetyTimer);
    safetyTimer = setTimeout(unhideInitial, Math.max(0, ms || 0));
  };
  const stopSafetyTimer = () => clearTimeout(safetyTimer);

  // -------------------------
  // Handle building
  // -------------------------
  const parseSegment = raw => {
    if (!raw || typeof raw !== 'string') return null;
    try {
      const obj = JSON.parse(raw);
      const id = obj?.segment_id?.toString().trim();
      return id ? { segmentId: id, raw: obj } : null;
    } catch {
      return null;
    }
  };

  const buildHandle = (segmentId, collectionId) => {
    const sid = segmentId?.toString().trim();
    const cid = collectionId?.toString().trim();
    return sid && cid ? `${sid}_${cid}` : '';
  };

  const pickHandleFromStorage = config => {
    const key = config?.storageKey || 'variant_segment';
    let raw = '';
    try {
      raw = localStorage.getItem(key) || '';
    } catch {}
    const parsed = parseSegment(raw);
    return parsed ? buildHandle(parsed.segmentId, config?.currentCollectionId) : '';
  };

  // -------------------------
  // DOM helpers
  // -------------------------
  const findMainSectionId = () => {
    const el = document.querySelector('[id^="shopify-section-"][id$="__main"]');
    return el ? el.id.replace(/^shopify-section-/, '') : null;
  };

  const findItemsContainer = root => {
    const selectors = [
      '#product-grid',
      '[data-product-grid]',
      'ul.product-grid',
      '.product-grid',
      '[id*="ProductGridContainer"] ul',
      '[id*="ProductGridContainer"] .product-grid',
    ];
    for (const sel of selectors) {
      const el = root.querySelector(sel);
      if (el?.children?.length != null) return el;
    }
    return null;
  };

  const looksLikeLiquidError = html => {
    if (!html) return true;
    const s = html.toLowerCase();
    return s.includes('liquid error') || s.includes('not paginateable');
  };

  // -------------------------
  // URL building
  // -------------------------
  const buildSectionsUrl = (handle, sectionIds, extraParams) => {
    const url = new URL(
      `${getRootPath()}collections/${encodeURIComponent(handle)}`,
      location.origin
    );

    // Carry current params
    new URLSearchParams(location.search).forEach((v, k) => {
      if (k !== 'sections' && k !== 'section_id') url.searchParams.append(k, v);
    });

    // Apply extra params
    extraParams?.forEach((v, k) => {
      if (k !== 'sections' && k !== 'section_id') url.searchParams.set(k, v);
    });

    url.searchParams.set('sections', sectionIds.join(','));
    return url.toString();
  };

  // -------------------------
  // Collection existence check (cached)
  // -------------------------
  const existsCache = new Map();

  const checkCollectionExists = async handle => {
    if (!handle) return false;
    if (existsCache.has(handle)) return existsCache.get(handle);

    try {
      const url = `${getRootPath()}collections/${encodeURIComponent(handle)}.json`;
      const res = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      const ok = res?.ok ?? false;
      existsCache.set(handle, ok);
      return ok;
    } catch {
      existsCache.set(handle, false);
      return false;
    }
  };

  // -------------------------
  // Infinite scroll
  // -------------------------
  let infiniteObserver = null;
  let isLoadingMore = false;
  let nextPage = 2;

  const teardownInfinite = () => {
    infiniteObserver?.disconnect();
    infiniteObserver = null;
    isLoadingMore = false;
    document.getElementById('VariantInfiniteScrollSentinel')?.remove();
  };

  const setupInfiniteScroll = (handle, mainId) => {
    teardownInfinite();

    const mainEl = document.getElementById(`shopify-section-${mainId}`);
    const itemsEl = mainEl && findItemsContainer(mainEl);
    if (!itemsEl) return;

    const currentPage = parseInt(new URLSearchParams(location.search).get('page') || '1', 10);
    nextPage = Number.isFinite(currentPage) ? currentPage + 1 : 2;

    const sentinel = document.createElement('div');
    sentinel.id = 'VariantInfiniteScrollSentinel';
    sentinel.style.cssText = 'height:1px;width:1px';
    (itemsEl.parentElement || mainEl).appendChild(sentinel);

    const loadNext = async () => {
      if (isLoadingMore) return;
      isLoadingMore = true;

      try {
        const extra = new URLSearchParams([['page', String(nextPage)]]);
        const url = buildSectionsUrl(handle, [mainId], extra);
        const res = await fetch(url, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);

        const data = await res.json();
        const html = data[mainId];

        if (!html || looksLikeLiquidError(html)) {
          teardownInfinite();
          return;
        }

        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const newItems = findItemsContainer(tmp);

        if (!newItems?.children.length) {
          teardownInfinite();
          return;
        }

        const frag = document.createDocumentFragment();
        Array.from(newItems.children).forEach(ch => frag.appendChild(ch));
        itemsEl.appendChild(frag);

        nextPage++;
        document.dispatchEvent(new CustomEvent('variant:infinite-products-appended'));
      } catch {
        teardownInfinite();
      } finally {
        isLoadingMore = false;
      }
    };

    infiniteObserver = new IntersectionObserver(([entry]) => entry?.isIntersecting && loadNext(), {
      rootMargin: '1200px 0px',
    });
    infiniteObserver.observe(sentinel);
  };

  // -------------------------
  // Interaction detection
  // -------------------------
  const hasCollectionParams = u => {
    if (u.searchParams.has('sort_by') || u.searchParams.has('page')) return true;
    for (const k of u.searchParams.keys()) if (k.startsWith('filter.')) return true;
    return false;
  };

  const isFacetControl = el => {
    if (!el?.closest) return false;
    const facetSelectors = [
      '.facets',
      'facet-filters-form',
      'facet-remove-component',
      'facet-link',
      '.active-facets',
      '.removable-facet',
    ];
    return (
      facetSelectors.some(s => el.closest(s)) ||
      el.classList?.contains('facets__clear-all') ||
      el.classList?.contains('facets__remove')
    );
  };

  const getSortByFromEvent = e => {
    const path = e.composedPath?.() || [];
    for (const node of path) {
      if (node?.getAttribute?.('role') === 'option') {
        const val = node.getAttribute('value');
        const inSort = path.some(n =>
          ['FACETS-SORT-POPOVER', 'X-LISTBOX'].includes(n?.tagName?.toUpperCase())
        );
        if (val && inSort) return val;
      }
    }
    return null;
  };

  const paramsFromForm = form => {
    const params = new URLSearchParams();
    for (const [k, v] of new FormData(form)) {
      if (typeof v === 'string' && v) params.append(k, v);
    }
    return params;
  };

  const isPaginationLink = url => {
    const newPage = url.searchParams.get('page');
    const currentPage = new URLSearchParams(location.search).get('page') || '1';
    return newPage && newPage !== currentPage;
  };

  // -------------------------
  // TOP 20 CAPTURE (before + after)
  // -------------------------
  let initialTop20 = [];
  let initialTop20Captured = false;

  let top20AfterSwap = [];
  let top20AfterSwapCaptured = false;

  // Expose arrays globally (saved “in the liquid” runtime, not returned)
  window.VariantTop20BeforeSwap = window.VariantTop20BeforeSwap || [];
  window.VariantTop20AfterSwap = window.VariantTop20AfterSwap || [];

  const normalizeProductHandleFromHref = href => {
    if (!href) return '';
    try {
      const u = new URL(href, location.origin);
      const m = u.pathname.match(/\/products\/([^\/]+)$/i);
      return m ? decodeURIComponent(m[1]) : '';
    } catch {
      return '';
    }
  };

  const captureTop20Now = () => {
    const mainRoot = cachedMainId
      ? document.getElementById(`shopify-section-${cachedMainId}`)
      : null;
    if (!mainRoot) return [];

    const itemsEl = findItemsContainer(mainRoot);
    const out = [];
    const seen = new Set();

    if (itemsEl?.children?.length) {
      for (const item of Array.from(itemsEl.children)) {
        if (out.length >= 20) break;

        const id =
          item.getAttribute?.('data-product-id') ||
          item.querySelector?.('[data-product-id]')?.getAttribute?.('data-product-id') ||
          '';

        const handleAttr =
          item.getAttribute?.('data-product-handle') ||
          item.querySelector?.('[data-product-handle]')?.getAttribute?.('data-product-handle') ||
          '';

        const a = item.querySelector?.('a[href*="/products/"]');
        const handleHref = a ? normalizeProductHandleFromHref(a.getAttribute('href')) : '';

        const handle = (handleAttr || handleHref || '').trim();
        const key = id ? `id:${id}` : handle ? `handle:${handle}` : '';
        if (!key || seen.has(key)) continue;

        seen.add(key);
        out.push({ handle: handle || null });
      }
    }

    if (out.length === 0) {
      const links = Array.from(mainRoot.querySelectorAll('a[href*="/products/"]'));
      for (const a of links) {
        if (out.length >= 20) break;
        const handle = normalizeProductHandleFromHref(a.getAttribute('href'));
        if (!handle) continue;
        const key = `handle:${handle}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ handle });
      }
    }

    return out;
  };

  const ensureInitialTop20Captured = () => {
    if (initialTop20Captured) return;
    initialTop20Captured = true; // Always set flag - only attempt once, even if capture fails

    const captured = captureTop20Now();
    if (captured && captured.length) {
      initialTop20 = captured;
      window.VariantTop20BeforeSwap = [...initialTop20];
    }
  };

  const captureTop20AfterSwap = () => {
    const captured = captureTop20Now();
    if (captured && captured.length) {
      top20AfterSwap = captured;
      top20AfterSwapCaptured = true;
      window.VariantTop20AfterSwap = [...top20AfterSwap];
    }
  };

  // -------------------------
  // Core state
  // -------------------------
  let controlsBound = false;
  let swapInFlight = null;
  let cachedMainId = null;
  let swapEnabled = false;
  let desiredHandle = '';
  let doSwapRef = null;
  let readyResolve = null;
  const ready = new Promise(r => (readyResolve = r));

  // experiment state saved globally
  window.VariantAttribution = window.VariantAttribution || {
    experiment_id: null,
    variation_id: 1,
  };

  // -------------------------
  // Public API
  // -------------------------
  window.VariantPrepareSwap = () => {
    const config = getConfig() || {};
    const maxMs = Number(config.initialHideMaxMs) || 2000;
    setClass('variant-segment-initial-loading', true);
    startSafetyTimer(maxMs);
  };

  // NOTE: SwapCollection now accepts (segmentId, experimentId) and does NOT return arrays.
  window.VariantSwapCollection = async (segmentId, experimentId) => {
    segmentId = 'e9c5eff0-c5cc-4cc4-9a22-bbc7e510d30f';
    await ready;
    const config = getConfig() || {};
    // save experiment info (passed from console)
    if (experimentId != null) window.VariantAttribution.experiment_id = String(experimentId);
    // allow theme to override variation if you want via config
    if (config?.variationId != null)
      window.VariantAttribution.variation_id = Number(config.variationId);

    desiredHandle = segmentId
      ? buildHandle(segmentId, config.currentCollectionId)
      : pickHandleFromStorage(config);

    if (!desiredHandle) {
      setGridLoading(false);
      unhideInitial();
      stopSafetyTimer();
      return { swapped: false };
    }

    setGridLoading(true);
    try {
      const ok = await doSwapRef();
      if (ok) swapEnabled = true;

      return { swapped: !!ok };
    } finally {
      setGridLoading(false);
      unhideInitial();
      stopSafetyTimer();
    }
  };

  // -------------------------
  // Attribution click listener (IN THIS FILE)
  // -------------------------
  let attributionBound = false;
  const productCache = {}; // handle -> product json

  async function getProductIdByHandle(handle) {
    if (productCache[handle]) return productCache[handle]?.id ?? null;

    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
    if (!res.ok) throw new Error(`Fetch failed for /products/${handle}.js (${res.status})`);

    const product = await res.json();
    productCache[handle] = product;
    return product?.id ?? null;
  }

  function safePublishAttribution(
    productId,
    handle,
    originalCollectionId,
    swappedCollectionHandle
  ) {
    const expId = window.VariantAttribution?.experiment_id;
    const varId = window.VariantAttribution?.variation_id ?? 1;

    if (!expId) {
      console.warn(
        '⚠️ Cannot publish attribution: missing experiment_id (call VariantSwapCollection(segmentId, experimentId) first)'
      );
      return false;
    }
    if (!productId) {
      console.warn('⚠️ Cannot publish attribution: missing product_id');
      return false;
    }
    if (!window.Variant || typeof window.Variant.publishCustomEvent !== 'function') {
      console.warn('⚠️ Variant.publishCustomEvent not available; event not fired');
      return false;
    }

    Variant.publishCustomEvent('merchandising-clicked-attribution', {
      experiment_id: expId,
      variation_id: varId,
      product_id: productId,
      product_handle: handle,
      original_collection_id: originalCollectionId || null,
      swapped_collection_handle: swappedCollectionHandle || null,
    });
    return true;
  }

  const bindAttributionOnce = () => {
    if (attributionBound) return;
    attributionBound = true;

    document.addEventListener(
      'click',
      async function (e) {
        // Only track clicks within the swapped main section
        const mainRoot = cachedMainId
          ? document.getElementById(`shopify-section-${cachedMainId}`)
          : null;
        if (!mainRoot) return;

        const path = e.composedPath?.() || [];
        const isInMain =
          path.some(n => n instanceof Node && mainRoot.contains(n)) ||
          (e.target instanceof Node && mainRoot.contains(e.target));
        if (!isInMain) return;

        const link = e.target.closest('a[href*="/products/"]');
        if (!link) return;

        // We only attribute when swap is enabled AND arrays exist
        if (!swapEnabled) return;

        const rawHandle = link.href.match(/\/products\/([^/?#]+)/)?.[1];
        if (!rawHandle) return;
        const handle = decodeURIComponent(rawHandle);

        const beforeArr = Array.isArray(window.VariantTop20BeforeSwap)
          ? window.VariantTop20BeforeSwap
          : [];
        const afterArr = Array.isArray(window.VariantTop20AfterSwap)
          ? window.VariantTop20AfterSwap
          : [];

        // build maps each click (arrays are tiny: max 20)
        const toIndexMap = arr => {
          const m = new Map();
          arr.forEach((item, i) => {
            const h = item?.handle != null ? String(item.handle).trim() : '';
            if (h && !m.has(h)) m.set(h, i);
          });
          return m;
        };

        const beforeIdx = toIndexMap(beforeArr);
        const afterIdx = toIndexMap(afterArr);

        const inBefore = beforeIdx.has(handle);
        const inAfter = afterIdx.has(handle);

        const beforeIndex = inBefore ? beforeIdx.get(handle) : null;
        const afterIndex = inAfter ? afterIdx.get(handle) : null;

        // Attribution logic: only fire event if product was added (not in before, yes in after)
        const shouldAttribute = !inBefore && inAfter;
        let productId = null;
        let eventFired = false;

        if (shouldAttribute) {
          // Prevent default navigation so the async event fires before redirect
          e.preventDefault();
          const destinationUrl = link.href;

          // Safety timeout: ensure user is never stuck if attribution fails
          const safetyNav = setTimeout(() => {
            window.location.href = destinationUrl;
          }, 2000);

          try {
            productId = await getProductIdByHandle(handle);
            const cfg = getConfig() || {};
            eventFired = safePublishAttribution(
              productId,
              handle,
              cfg.currentCollectionId,
              desiredHandle
            );
          } catch (err) {
            console.error('[Variant Attribution] Failed to fetch product_id', { handle, err });
          }

          clearTimeout(safetyNav);
          window.location.href = destinationUrl;
        }

        console.log('[Variant Attribution]', {
          handle,
          productId,
          beforeIndex,
          afterIndex,
          inBefore,
          inAfter,
          eventFired,
          originalCollectionId: (getConfig() || {}).currentCollectionId || null,
          swappedCollectionHandle: desiredHandle || null,
        });
      },
      true
    );
  };

  // -------------------------
  // Boot
  // -------------------------
  const boot = async () => {
    const config = getConfig() || {};

    if (document.documentElement.classList.contains('variant-segment-initial-loading')) {
      const maxMs = Number(config.initialHideMaxMs) || 2000;
      startSafetyTimer(maxMs);
    }

    cachedMainId = findMainSectionId();
    if (!cachedMainId) {
      // Main section not found - log error and assign no-op
      console.error(
        '[Variant Merchandising] Cannot initialize: Main collection section not found. Looking for element with id="shopify-section-*__main"'
      );

      readyResolve?.();
      unhideInitial();
      stopSafetyTimer();
      return;
    }

    const doSwap = async () => {
      if (!desiredHandle) return false;
      if (!(await checkCollectionExists(desiredHandle))) {
        console.log('no segmented collection exists');
        return false;
      }
      if (swapInFlight) return swapInFlight;

      swapInFlight = (async () => {
        try {
          // Capture TOP 20 from ORIGINAL section right before replace
          ensureInitialTop20Captured();

          const url = buildSectionsUrl(desiredHandle, [cachedMainId]);
          const res = await fetch(url, {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });

          if (!res.ok) return false;

          const data = await res.json();
          const html = data[cachedMainId];

          if (!html || looksLikeLiquidError(html)) return false;

          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          const newSection = tmp.querySelector(`#shopify-section-${CSS.escape(cachedMainId)}`);

          if (!newSection) return false;

          document.getElementById(`shopify-section-${cachedMainId}`)?.replaceWith(newSection);
          document.dispatchEvent(
            new CustomEvent('shopify:section:load', { detail: { sectionId: cachedMainId } })
          );
          setupInfiniteScroll(desiredHandle, cachedMainId);

          // Capture TOP 20 after swap (now that new section is in DOM)
          captureTop20AfterSwap();

          // bind attribution once swap logic exists
          bindAttributionOnce();

          return true;
        } catch {
          return false;
        }
      })();

      try {
        return await swapInFlight;
      } finally {
        swapInFlight = null;
      }
    };

    doSwapRef = doSwap;
    readyResolve?.();

    // -------------------------
    // Event bindings (once)
    // -------------------------
    if (controlsBound) return;
    controlsBound = true;

    const getMainRoot = () =>
      cachedMainId ? document.getElementById(`shopify-section-${cachedMainId}`) : null;

    const isInMain = e => {
      const root = getMainRoot();
      if (!root) return false;
      const path = e.composedPath?.() || [];
      return (
        path.some(n => n instanceof Node && root.contains(n)) ||
        (e.target instanceof Node && root.contains(e.target))
      );
    };

    const handleInteraction = async (newParams, scrollToTop = false) => {
      const newUrl = new URL(location.href);
      newUrl.search = newParams.toString();
      history.pushState({}, '', newUrl.toString());

      setGridLoading(true);
      try {
        await doSwap();
        if (scrollToTop) {
          const mainEl = getMainRoot();
          if (mainEl) mainEl.scrollIntoView({ behavior: 'instant', block: 'start' });
          else window.scrollTo(0, 0);
        }
      } finally {
        setGridLoading(false);
      }
    };

    window.addEventListener(
      'click',
      async e => {
        if (!swapEnabled || !isInMain(e)) return;

        const sortBy = getSortByFromEvent(e);
        if (sortBy) {
          e.preventDefault();
          e.stopImmediatePropagation();
          const p = new URLSearchParams(location.search);
          p.set('sort_by', sortBy);
          p.delete('page');
          return handleInteraction(p, false);
        }

        const dataUrlEl = e.target?.closest?.('[data-url]');
        if (dataUrlEl) {
          const raw = dataUrlEl.getAttribute('data-url');
          try {
            const u = new URL(raw, location.origin);
            if (u.pathname.startsWith('/collections/')) {
              e.preventDefault();
              e.stopImmediatePropagation();
              return handleInteraction(new URLSearchParams(u.search), isPaginationLink(u));
            }
          } catch {}
        }

        const a = e.target?.closest?.('a[href]');
        if (!a) return;

        try {
          const u = new URL(a.getAttribute('href'), location.origin);
          if (
            u.pathname.startsWith('/collections/') &&
            (hasCollectionParams(u) || isFacetControl(a))
          ) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return handleInteraction(new URLSearchParams(u.search), isPaginationLink(u));
          }
        } catch {}
      },
      true
    );

    window.addEventListener(
      'submit',
      async e => {
        if (!swapEnabled) return;
        const form = e.target?.closest?.('form');
        const root = getMainRoot();
        if (!form || (root && !root.contains(form)) || form.method?.toLowerCase() !== 'get') return;

        e.preventDefault();
        e.stopImmediatePropagation();
        const p = paramsFromForm(form);
        p.delete('page');
        return handleInteraction(p, false);
      },
      true
    );

    window.addEventListener(
      'change',
      async e => {
        if (!swapEnabled) return;
        const el = e.target;
        const root = getMainRoot();
        if (!el?.getAttribute || (root && !root.contains(el))) return;

        const name = el.getAttribute('name') || '';
        if (name !== 'sort_by' && !name.startsWith('filter.')) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        const form = el.closest('form');
        const p = form ? paramsFromForm(form) : new URLSearchParams(location.search);
        p.delete('page');
        return handleInteraction(p, false);
      },
      true
    );

    window.addEventListener('popstate', async () => {
      if (!swapEnabled) return;
      setGridLoading(true);
      try {
        await doSwap();
        const mainEl = getMainRoot();
        if (mainEl) mainEl.scrollIntoView({ behavior: 'instant', block: 'start' });
      } finally {
        setGridLoading(false);
      }
    });
  };

  boot().catch(() => {
    setGridLoading(false);
    unhideInitial();
    stopSafetyTimer();
  });
})();
